import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface TestSession {
  id: string;
  date: string;
  eps: number;
  engineScore: number;
  ignitionScore: number;
  bounceScore: number;
  trapBarDL: number;
  backSquat: number;
  cmjHeight: number;
  sprint10yd: number;
  rsi: number;
  broadJump: number;
  dropJumpHeight: number;
}

interface BeforeAfterTableProps {
  first: TestSession;
  latest: TestSession;
}

const ROWS = [
  { label: 'EPS', firstKey: 'eps', unit: '', category: 'Overall' },
  { label: 'Engine Score', firstKey: 'engineScore', unit: '', category: 'Engine' },
  { label: 'Ignition Score', firstKey: 'ignitionScore', unit: '', category: 'Ignition' },
  { label: 'Bounce Score', firstKey: 'bounceScore', unit: '', category: 'Bounce' },
  { label: 'Trap Bar DL', firstKey: 'trapBarDL', unit: 'lbs', category: 'Engine' },
  { label: 'Back Squat', firstKey: 'backSquat', unit: 'lbs', category: 'Engine' },
  { label: 'CMJ Height', firstKey: 'cmjHeight', unit: 'in', category: 'Ignition' },
  { label: '10-Yard Sprint', firstKey: 'sprint10yd', unit: 's', category: 'Ignition' },
  { label: 'RSI', firstKey: 'rsi', unit: '', category: 'Bounce' },
  { label: 'Broad Jump', firstKey: 'broadJump', unit: 'in', category: 'Bounce' },
  { label: 'Drop Jump', firstKey: 'dropJumpHeight', unit: 'in', category: 'Bounce' },
];

const CAT_COLOR: Record<string, string> = {
  Overall: '#FF3B30',
  Engine: '#8B0000',
  Ignition: '#F59E0B',
  Bounce: '#B3B3B3',
};

const LOWER_IS_BETTER = ['sprint10yd', 'sprint20yd'];

export default function BeforeAfterTable({ first, latest }: BeforeAfterTableProps) {
  const firstDate = new Date(first.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const latestDate = new Date(latest.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="card p-5">
      <h3 className="section-header mb-4">Before vs After Comparison</h3>
      <div className="overflow-x-auto scrollbar-thin rounded-xl border border-zinc-800">
        <table className="w-full text-sm min-w-[480px]">
          <thead>
            <tr className="border-b border-zinc-800 bg-zinc-800/40">
              <th className="text-left py-2.5 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">Metric</th>
              <th className="text-left py-2.5 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">Category</th>
              <th className="text-left py-2.5 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">
                <span className="font-mono">{firstDate}</span>
              </th>
              <th className="text-left py-2.5 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">
                <span className="font-mono">{latestDate}</span>
              </th>
              <th className="text-left py-2.5 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500">Δ Change</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map(row => {
              const firstVal = (first as any)[row.firstKey] as number;
              const latestVal = (latest as any)[row.firstKey] as number;
              const rawDelta = latestVal - firstVal;
              const lowerBetter = LOWER_IS_BETTER.includes(row.firstKey);
              const isPositive = lowerBetter ? rawDelta < 0 : rawDelta > 0;
              const isNegative = lowerBetter ? rawDelta > 0 : rawDelta < 0;
              const deltaDisplay = rawDelta > 0 ? `+${rawDelta.toFixed(2).replace(/\.?0+$/, '')}` : rawDelta.toFixed(2).replace(/\.?0+$/, '');
              const catColor = CAT_COLOR[row.category];

              return (
                <tr key={`ba-${row.firstKey}`} className="border-b border-zinc-800/40 hover:bg-zinc-800/30 transition-colors">
                  <td className="py-2.5 px-3 text-zinc-300 font-medium">{row.label}</td>
                  <td className="py-2.5 px-3">
                    <span className="text-xs font-semibold" style={{ color: catColor }}>{row.category}</span>
                  </td>
                  <td className="py-2.5 px-3 font-mono tabular-nums text-zinc-400">{firstVal}{row.unit ? ` ${row.unit}` : ''}</td>
                  <td className="py-2.5 px-3 font-mono tabular-nums text-zinc-200 font-semibold">{latestVal}{row.unit ? ` ${row.unit}` : ''}</td>
                  <td className="py-2.5 px-3">
                    <div className={`flex items-center gap-1 font-mono tabular-nums text-sm font-semibold ${isPositive ? 'text-emerald-400' : isNegative ? 'text-red-400' : 'text-zinc-500'}`}>
                      {isPositive ? <TrendingUp size={12} /> : isNegative ? <TrendingDown size={12} /> : <Minus size={12} />}
                      {deltaDisplay}{row.unit ? ` ${row.unit}` : ''}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}